import { useState } from 'react';
import { Languages, Check, Search, Loader2 } from 'lucide-react';
import PageHeader from '../../components/settings/PageHeader';
import SettingCard from '../../components/settings/SettingCard';
import { useSettingsStore } from '../../store/settingsStore';
import { useTranslation } from '../../hooks/useTranslation';
import { LANGUAGE_PACKS, TRANSLATED_LANGUAGES } from '../../services/localizationService';
import { cn } from '../../utils/helpers';

interface Lang {
  code: string;
  /** Endonym, always shown in the language itself. */
  native: string;
  /** Default region applied when this language is chosen. */
  region: string;
}

/** Languages without a dedicated bundle yet — picking one falls back to English text. */
const UNTRANSLATED: Lang[] = [
  { code: 'it', native: 'Italiano', region: 'IT' },
  { code: 'ar', native: 'العربية', region: 'SA' },
  { code: 'sw', native: 'Kiswahili', region: 'KE' },
];

// All languages with a full translation bundle, sourced from the single
// LANGUAGE_PACKS list in localizationService (en/am first, then the rest
// alphabetically by code), plus a handful of still-untranslated options.
const LANGUAGES: Lang[] = [
  ...LANGUAGE_PACKS.filter((p) => p.code === 'en' || p.code === 'am'),
  ...LANGUAGE_PACKS.filter((p) => p.code !== 'en' && p.code !== 'am').sort((a, b) => a.code.localeCompare(b.code)),
].map((p) => ({ code: p.code, native: p.native, region: p.defaultRegion }));
LANGUAGES.push(...UNTRANSLATED);

const fullyTranslated = (code: string) => (TRANSLATED_LANGUAGES as readonly string[]).includes(code);

export default function Language() {
  const { data, savePreferences, saving } = useSettingsStore();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const selected = data?.preferences.language ?? 'en';

  const filtered = LANGUAGES.filter(
    (l) =>
      t(`languages.${l.code}`).toLowerCase().includes(query.toLowerCase()) ||
      l.native.toLowerCase().includes(query.toLowerCase()),
  );

  // Saving the preference also updates the localization service (via the
  // store's applyAppearance), so the whole UI re-renders in the new language
  // and the region snaps to that language's default (still user-adjustable
  // afterwards from the region selector on the Preferences page).
  const choose = (lang: Lang) => savePreferences({ language: lang.code, region: lang.region });

  return (
    <div className="space-y-6 max-w-xl">
      <PageHeader title={t('settings.language.title')} subtitle={t('settings.language.subtitle')} icon={Languages} backTo="/app/settings/preferences" />

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 dark:text-neutral-500" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('settings.language.searchPlaceholder')}
          className="w-full pl-11 pr-4 py-3 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
        />
      </div>

      <SettingCard flush>
        {filtered.map((lang) => (
          <button
            key={lang.code}
            onClick={() => choose(lang)}
            className="w-full flex items-center justify-between px-5 py-3.5 border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors"
          >
            <div className="text-left">
              <p className="font-semibold text-sm text-neutral-900 dark:text-neutral-50">
                {lang.native} <span className="font-normal text-neutral-400 dark:text-neutral-500">({t(`languages.${lang.code}`)})</span>
                {fullyTranslated(lang.code) && (
                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded-md">
                    {t('settings.language.fullyTranslated')}
                  </span>
                )}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{t(`regions.${lang.region}`)}</p>
            </div>
            {selected === lang.code ? (
              saving ? <Loader2 className="w-5 h-5 animate-spin text-neutral-400 dark:text-neutral-500" /> : <Check className="w-5 h-5 text-neutral-900 dark:text-neutral-50" />
            ) : (
              <span className={cn('text-xs font-mono text-neutral-300')}>{lang.region}</span>
            )}
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="px-5 py-8 text-center text-sm text-neutral-400 dark:text-neutral-500">{t('settings.language.noMatch', { query })}</p>
        )}
      </SettingCard>
    </div>
  );
}
